/**
 * Rent vs Buy Housing Calculator — educational buy / rent / invest model.
 * Mortgage payment: Excel PMT type=1 (beginning of period).
 * Growth rates: simple monthly = annual / 12.
 */

export const MILESTONE_YEARS = Object.freeze([1, 5, 10, 15, 20, 25, 30]);
export const DOWN_PAYMENT_PERCENTS = Object.freeze([0.05, 0.1, 0.15, 0.2]);
export const CHART_YEARS = Object.freeze([0, 1, 5, 10, 15, 20, 25, 30]);

export const FILING_STATUS_OPTIONS = Object.freeze([
  { value: 'single', label: 'Single', standardDeduction: 16100 },
  { value: 'mfj', label: 'Married filing jointly', standardDeduction: 32200 },
  { value: 'hoh', label: 'Head of household', standardDeduction: 24150 },
  { value: 'mfs', label: 'Married filing separately', standardDeduction: 16100 },
]);

export const DEFAULT_SALT_CAP = 40400;

export const DEFAULT_HOUSING_INPUTS = Object.freeze({
  homeValue: '500000',
  downPayment: '100000',
  monthlyRent: '2200',
  annualRentIncrease: '3',
  annualInterestRate: '6.5',
  loanTermMonths: '360',
  loanStartDate: '',
  annualAppreciationRate: '3',
  annualInvestmentReturn: '9',
  pmiRate: '0.5',
  propertyTaxRate: '1.0',
  homeownersInsuranceRate: '0.35',
  maintenanceRate: '1.0',
  closingCostRate: '3.0',
  sellingCostRate: '6.0',
  monthlyHoa: '0',
  availableCash: '',
  filingStatus: 'mfj',
});

/** Always-on payment strategies shown in results (no user toggle). */
export const PAYMENT_STRATEGIES = Object.freeze([
  { id: 'standard', label: 'Standard monthly', shortLabel: 'Standard' },
  { id: 'one_extra', label: '1 extra payment / year', shortLabel: '1 extra' },
  { id: 'two_extra', label: '2 extra payments / year', shortLabel: '2 extras' },
  { id: 'biweekly', label: 'Bi-weekly (26 half-payments)', shortLabel: 'Bi-weekly' },
]);

/** Fixed educational tax assumptions — not personalized inputs. */
export const DEFAULT_TAX_ASSUMPTIONS = Object.freeze({
  federalMarginalRate: 0.24,
  includeSaltCap: true,
  saltCap: DEFAULT_SALT_CAP,
  // SALT uses property tax only in this simplified model (no state-income-tax input).
  stateTaxForSalt: 0,
});

/** Excel PMT. Returns a positive payment amount for a loan (pv > 0). */
export function excelPmt(rate, nper, pv, fv = 0, type = 1) {
  const n = Number(nper);
  const r = Number(rate);
  const present = Number(pv);
  const future = Number(fv);
  if (!(n > 0) || !Number.isFinite(present)) return 0;
  if (!Number.isFinite(r) || r === 0) return (present + future) / n;
  const factor = (1 + r) ** n;
  let pmt = (r * (present * factor + future)) / (factor - 1);
  if (type === 1) pmt /= 1 + r;
  return pmt;
}

function parseNonNegative(value) {
  if (value === '' || value == null) return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

function parsePositive(value) {
  const n = parseNonNegative(value);
  if (n == null || n <= 0) return null;
  return n;
}

function parseRatePercent(value) {
  const n = parseNonNegative(value);
  if (n == null) return null;
  return n / 100;
}

function parseStartDate(value) {
  if (!value) {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }
  const match = String(value).match(/^(\d{4})-(\d{2})(?:-(\d{2}))?$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3] || 1);
  const date = new Date(year, month, day);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, date.getDate());
}

function formatIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function roundMoney(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

function formatUsdPlain(value) {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDatePlain(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export function standardDeductionForStatus(filingStatus) {
  const match = FILING_STATUS_OPTIONS.find((opt) => opt.value === filingStatus);
  return match ? match.standardDeduction : 16100;
}

/**
 * PMI: $0 when down payment ≥ 20%. Otherwise original loan × rate / 12 while LTV > 80%.
 */
export function monthlyPmiForBalance({
  originalLoanAmount,
  loanBalance,
  homeValue,
  pmiRate,
  originalDownPct,
}) {
  if (originalDownPct >= 0.2) return 0;
  if (!(pmiRate > 0) || !(originalLoanAmount > 0) || !(loanBalance > 0) || !(homeValue > 0)) return 0;
  if (loanBalance / homeValue <= 0.8) return 0;
  return (originalLoanAmount * pmiRate) / 12;
}

export function afterSaleEquity(homeValue, mortgageBalance, sellingCostRate) {
  const netSale = homeValue * (1 - sellingCostRate);
  return Math.max(0, netSale - Math.max(0, mortgageBalance));
}

function rowAtYear(rows, year) {
  if (!rows?.length) return null;
  if (year <= 0) return null;
  const idx = Math.min(year * 12 - 1, rows.length - 1);
  return rows[idx];
}

function sumInterestInYear(rows, year) {
  const start = (year - 1) * 12;
  const end = year * 12;
  let sum = 0;
  for (let i = start; i < end && i < rows.length; i += 1) sum += rows[i].interest;
  return sum;
}

function sumFieldInYear(rows, year, field) {
  const start = (year - 1) * 12;
  const end = year * 12;
  let sum = 0;
  for (let i = start; i < end && i < rows.length; i += 1) sum += rows[i][field] || 0;
  return sum;
}

/**
 * Incremental federal tax benefit from itemizing (simplified educational estimate).
 */
export function estimateAnnualTaxBenefit({
  mortgageInterest,
  propertyTax,
  estimatedStateTax,
  useItemizedEstimate,
  includeSaltCap,
  saltCap,
  standardDeduction,
  federalMarginalRate,
}) {
  if (!useItemizedEstimate || !(federalMarginalRate > 0)) {
    return {
      saltUsed: 0,
      itemizedDeductions: 0,
      standardDeduction,
      exceedsStandard: false,
      incrementalTaxBenefit: 0,
    };
  }

  const rawSalt = Math.max(0, propertyTax) + Math.max(0, estimatedStateTax);
  const saltUsed = includeSaltCap ? Math.min(rawSalt, saltCap) : rawSalt;
  const itemizedDeductions = Math.max(0, mortgageInterest) + saltUsed;
  const excess = Math.max(0, itemizedDeductions - standardDeduction);
  return {
    saltUsed: roundMoney(saltUsed),
    itemizedDeductions: roundMoney(itemizedDeductions),
    standardDeduction: roundMoney(standardDeduction),
    exceedsStandard: excess > 0,
    incrementalTaxBenefit: roundMoney(excess * federalMarginalRate),
  };
}

/**
 * @param {'standard'|'one_extra'|'two_extra'|'biweekly'} paymentStrategy
 * - one_extra: one full payment every 12 months
 * - two_extra: one full payment every 6 months
 * - biweekly: 26 half-payments/year ≈ monthly P&I × 13/12
 */
export function buildBuySchedule({
  homeValue0,
  loanAmount,
  downPayment,
  monthlyRate,
  termMonths,
  basePayment,
  startDate,
  monthlyAppreciation,
  monthlyInvestReturn,
  monthlyRent0 = 0,
  annualRentIncrease = 0,
  pmiRate,
  propertyTaxRate,
  insuranceRate,
  maintenanceRate,
  monthlyHoa,
  sellingCostRate,
  paymentStrategy = 'standard',
  horizonMonths,
  startingSideInvest = 0,
  id = 'buy',
  label = 'Buy',
}) {
  const originalDownPct = homeValue0 > 0 ? downPayment / homeValue0 : 0;
  const originalLoanAmount = loanAmount;
  // Bi-weekly: 26 × (PMT/2) = 13 full payments/year → model as PMT × 13/12 each month.
  const scheduledPi = paymentStrategy === 'biweekly' ? basePayment * (13 / 12) : basePayment;
  const extraEvery = paymentStrategy === 'one_extra' ? 12 : paymentStrategy === 'two_extra' ? 6 : 0;
  const extraAmount = extraEvery > 0 ? basePayment : 0;

  let balance = loanAmount;
  let home = homeValue0;
  let invest = startingSideInvest;
  let rent = monthlyRent0;
  let cumInterest = 0;
  let cumPmi = 0;
  let cumTax = 0;
  let cumInsurance = 0;
  let cumMaintenance = 0;
  let cumHoa = 0;
  let cumOwnership = 0;
  let payoffMonth = null;
  let payoffDate = null;
  const rows = [];

  for (let month = 1; month <= horizonMonths; month += 1) {
    if (month > 1 && (month - 1) % 12 === 0) {
      rent *= 1 + annualRentIncrease;
    }

    const beginningBalance = Math.max(0, balance);
    const interest = beginningBalance > 0 ? beginningBalance * monthlyRate : 0;
    const isExtraMonth =
      extraEvery > 0 && extraAmount > 0 && month % extraEvery === 0 && beginningBalance > 0;

    let payment = 0;
    let extraApplied = 0;

    if (beginningBalance > 0) {
      payment = scheduledPi;
      if (isExtraMonth) {
        extraApplied = extraAmount;
        payment += extraApplied;
      }
      const maxPayment = beginningBalance + interest;
      if (payment > maxPayment) {
        payment = maxPayment;
        extraApplied = Math.max(0, payment - scheduledPi);
      }
    }

    let principal = Math.max(0, payment - interest);
    if (principal > beginningBalance) {
      principal = beginningBalance;
      payment = principal + interest;
    }

    const endingBalance = Math.max(0, beginningBalance - principal);
    cumInterest += interest;

    home *= 1 + monthlyAppreciation;
    const grossEquity = Math.max(0, home - endingBalance);
    const saleEquity = afterSaleEquity(home, endingBalance, sellingCostRate);

    const tax = (home * propertyTaxRate) / 12;
    const insurance = (home * insuranceRate) / 12;
    const maintenance = (home * maintenanceRate) / 12;
    const hoa = monthlyHoa;
    const pmi = monthlyPmiForBalance({
      originalLoanAmount,
      loanBalance: beginningBalance,
      homeValue: home,
      pmiRate,
      originalDownPct,
    });

    cumTax += tax;
    cumInsurance += insurance;
    cumMaintenance += maintenance;
    cumHoa += hoa;
    cumPmi += pmi;

    const pi = beginningBalance > 0 ? scheduledPi : 0;
    const ownershipExtras = tax + pmi + insurance + maintenance + hoa + extraApplied;
    const housingCost = pi + ownershipExtras;
    cumOwnership += housingCost;

    // Invest only the monthly cash-flow gap vs rent (no separate contribution input).
    const cashFlowGapVsRent = rent - housingCost; // positive when owning costs less
    invest = invest * (1 + monthlyInvestReturn);
    invest = Math.max(0, invest + cashFlowGapVsRent);

    const paymentDate = addMonths(startDate, month - 1);
    // Holding NW uses after-sale equity for apples-to-apples vs liquid rent investments.
    const netWorth = saleEquity + invest;

    rows.push({
      month,
      date: formatIsoDate(paymentDate),
      beginningBalance: roundMoney(beginningBalance),
      payment: roundMoney(payment),
      principal: roundMoney(principal),
      interest: roundMoney(interest),
      endingBalance: roundMoney(endingBalance),
      homeValue: roundMoney(home),
      equityAmount: roundMoney(grossEquity),
      afterSaleEquity: roundMoney(saleEquity),
      investmentValue: roundMoney(invest),
      netWorth: roundMoney(netWorth),
      cumulativeInterest: roundMoney(cumInterest),
      cumulativePmi: roundMoney(cumPmi),
      cumulativePropertyTax: roundMoney(cumTax),
      cumulativeInsurance: roundMoney(cumInsurance),
      cumulativeMaintenance: roundMoney(cumMaintenance),
      cumulativeHoa: roundMoney(cumHoa),
      cumulativeOwnershipCosts: roundMoney(cumOwnership),
      cumulativeRent: 0,
      monthlyHousingCost: roundMoney(housingCost),
      monthlyPi: roundMoney(pi),
      monthlyPmi: roundMoney(pmi),
      monthlyPropertyTax: roundMoney(tax),
      monthlyInsurance: roundMoney(insurance),
      monthlyMaintenance: roundMoney(maintenance),
      monthlyHoa: roundMoney(hoa),
      monthlyRent: roundMoney(rent),
      monthlyInvestedFromCashFlow: roundMoney(Math.max(0, cashFlowGapVsRent)),
      cashFlowGap: roundMoney(cashFlowGapVsRent),
      extraApplied: roundMoney(extraApplied),
    });

    balance = endingBalance;
    if (balance <= 0.005 && payoffMonth == null) {
      balance = 0;
      payoffMonth = month;
      payoffDate = formatIsoDate(paymentDate);
    }
  }

  if (payoffMonth == null) {
    payoffMonth = termMonths;
    payoffDate = formatIsoDate(addMonths(startDate, termMonths - 1));
  }

  const first = rows[0];
  return {
    id,
    label,
    paymentStrategy,
    rows,
    payoffMonth,
    payoffDate,
    totalInterest: roundMoney(cumInterest),
    basePayment: roundMoney(basePayment),
    scheduledMonthlyPi: roundMoney(scheduledPi),
    loanAmount: roundMoney(loanAmount),
    downPayment: roundMoney(downPayment),
    costBreakdown: {
      principalAndInterest: roundMoney(scheduledPi),
      propertyTax: roundMoney(first?.monthlyPropertyTax || 0),
      pmi: roundMoney(first?.monthlyPmi || 0),
      insurance: roundMoney(first?.monthlyInsurance || 0),
      maintenance: roundMoney(first?.monthlyMaintenance || 0),
      hoa: roundMoney(monthlyHoa),
      total: roundMoney(first?.monthlyHousingCost || 0),
    },
    startingMonthlyHousing: first ? first.monthlyHousingCost : 0,
    startingMonthlyPmi: first ? first.monthlyPmi : 0,
    startingMonthlyTax: first ? first.monthlyPropertyTax : 0,
  };
}

export function buildRentSchedule({
  startingInvest,
  monthlyRent0,
  annualRentIncrease,
  monthlyInvestReturn,
  buyRows,
  horizonMonths,
  startDate,
}) {
  let invest = startingInvest;
  let rent = monthlyRent0;
  let cumRent = 0;
  const rows = [];

  for (let month = 1; month <= horizonMonths; month += 1) {
    if (month > 1 && (month - 1) % 12 === 0) {
      rent *= 1 + annualRentIncrease;
    }

    const buyRow = buyRows[month - 1];
    const ownCost = buyRow ? buyRow.monthlyHousingCost : 0;
    // Positive when owning costs more than renting → that difference is invested if renting.
    const cashFlowGap = ownCost - rent;

    invest = invest * (1 + monthlyInvestReturn);
    invest = Math.max(0, invest + cashFlowGap);
    cumRent += rent;

    rows.push({
      month,
      date: formatIsoDate(addMonths(startDate, month - 1)),
      beginningBalance: 0,
      payment: 0,
      principal: 0,
      interest: 0,
      endingBalance: 0,
      homeValue: 0,
      equityAmount: 0,
      afterSaleEquity: 0,
      investmentValue: roundMoney(invest),
      netWorth: roundMoney(invest),
      cumulativeInterest: 0,
      cumulativePmi: 0,
      cumulativePropertyTax: 0,
      cumulativeInsurance: 0,
      cumulativeMaintenance: 0,
      cumulativeHoa: 0,
      cumulativeOwnershipCosts: 0,
      cumulativeRent: roundMoney(cumRent),
      monthlyHousingCost: roundMoney(rent),
      monthlyPi: 0,
      monthlyPmi: 0,
      monthlyPropertyTax: 0,
      monthlyInsurance: 0,
      monthlyMaintenance: 0,
      monthlyHoa: 0,
      monthlyRent: roundMoney(rent),
      monthlyInvestedFromCashFlow: roundMoney(Math.max(0, cashFlowGap)),
      cashFlowGap: roundMoney(cashFlowGap),
      extraApplied: 0,
    });
  }

  return {
    id: 'rent',
    label: 'Rent & invest',
    rows,
    payoffMonth: null,
    payoffDate: null,
    totalInterest: 0,
    startingMonthlyRent: roundMoney(monthlyRent0),
    startingInvest: roundMoney(startingInvest),
  };
}

function buildDownPaymentOption({
  homeValue,
  downPct,
  label,
  availableCash,
  closingCostRate,
  monthlyRate,
  termMonths,
  startDate,
  monthlyAppreciation,
  monthlyInvestReturn,
  monthlyRent0,
  annualRentIncrease,
  pmiRate,
  propertyTaxRate,
  insuranceRate,
  maintenanceRate,
  monthlyHoa,
  sellingCostRate,
  horizonMonths,
  milestoneYears,
}) {
  const downPayment = roundMoney(homeValue * downPct);
  const closingCosts = roundMoney(homeValue * closingCostRate);
  const loanAmount = roundMoney(Math.max(0, homeValue - downPayment));
  const basePayment = loanAmount > 0 ? excelPmt(monthlyRate, termMonths, loanAmount, 0, 1) : 0;
  const cashToBuy = roundMoney(downPayment + closingCosts);
  const cashLeftInvested = roundMoney(Math.max(0, availableCash - cashToBuy));

  const shared = {
    homeValue0: homeValue,
    loanAmount,
    downPayment,
    monthlyRate,
    termMonths,
    basePayment,
    startDate,
    monthlyAppreciation,
    monthlyInvestReturn,
    monthlyRent0,
    annualRentIncrease,
    pmiRate,
    propertyTaxRate,
    insuranceRate,
    maintenanceRate,
    monthlyHoa,
    sellingCostRate,
    horizonMonths,
    startingSideInvest: cashLeftInvested,
  };

  const byStrategy = {};
  PAYMENT_STRATEGIES.forEach((strategy) => {
    const schedule = buildBuySchedule({
      ...shared,
      paymentStrategy: strategy.id,
      id: strategy.id,
      label: strategy.label,
    });
    const netWorthByYear = {};
    milestoneYears.forEach((year) => {
      const row = rowAtYear(schedule.rows, year);
      netWorthByYear[year] = row ? row.netWorth : 0;
    });
    byStrategy[strategy.id] = {
      ...strategy,
      payoffDate: schedule.payoffDate,
      payoffMonth: schedule.payoffMonth,
      totalInterest: schedule.totalInterest,
      netWorthByYear,
      netWorthAt30: netWorthByYear[30] || 0,
    };
  });

  const buy = buildBuySchedule({
    ...shared,
    paymentStrategy: 'standard',
    id: 'standard',
    label: 'Standard monthly',
  });

  return {
    label,
    downPct,
    downPayment,
    closingCosts,
    loanAmount,
    monthlyPayment: roundMoney(basePayment),
    monthlyPmi: buy.costBreakdown.pmi,
    monthlyPropertyTax: buy.costBreakdown.propertyTax,
    totalMonthlyHousing: buy.costBreakdown.total,
    cashLeftInvested,
    cashToBuy,
    byStrategy,
    netWorthByYear: byStrategy.standard.netWorthByYear,
    netWorthAt30: byStrategy.standard.netWorthAt30,
  };
}

export function buildInterpretation(projection) {
  const {
    bestPath,
    buyVsRentDeltaAt30,
    breakEvenYear,
    paymentStrategies,
    downPaymentLeader,
    ownershipCostBreakdown,
  } = projection;

  const oneExtra = paymentStrategies?.one_extra;
  const biweekly = paymentStrategies?.biweekly;

  let headline;
  if (bestPath === 'rent') {
    headline =
      'Under these assumptions, renting and investing available housing cash projects higher net worth than buying — often when investment returns, flexibility, and lower monthly costs outweigh after-sale home equity.';
  } else {
    headline =
      'Under these assumptions, buying projects higher net worth than renting — often when appreciation, leverage, and a long holding period outweigh interest, taxes, insurance, maintenance, PMI, and selling costs.';
  }

  const bullets = [
    buyVsRentDeltaAt30 > 500
      ? `Buying leads renting by about ${formatUsdPlain(Math.abs(buyVsRentDeltaAt30))} in projected net worth at year 30 (using estimated after-sale equity).`
      : buyVsRentDeltaAt30 < -500
        ? `Renting & investing leads buying by about ${formatUsdPlain(Math.abs(buyVsRentDeltaAt30))} in projected net worth at year 30.`
        : 'Buying and renting land in a similar place at year 30 under these assumptions.',
    `Monthly ownership costs start near ${formatUsdPlain(ownershipCostBreakdown.total)} (P&I, property tax, PMI, insurance, maintenance, HOA). The calculator invests the monthly difference versus rent — not a separate contribution amount.`,
    breakEvenYear
      ? `The projected break-even year between buying and renting is around year ${breakEvenYear}.`
      : 'There is no clear break-even year within the 30-year window — one path stays ahead throughout.',
    oneExtra
      ? `One extra payment per year may move payoff to ${formatDatePlain(oneExtra.payoffDate)} and save about ${formatUsdPlain(oneExtra.interestSaved)} in interest versus the standard schedule.`
      : null,
    biweekly
      ? `Bi-weekly payments (26 half-payments ≈ 13 monthly payments/year) may move payoff to ${formatDatePlain(biweekly.payoffDate)} and save about ${formatUsdPlain(biweekly.interestSaved)} in interest.`
      : null,
    'Down payment size affects PMI, monthly payment, liquidity, and how much cash stays invested.',
    downPaymentLeader
      ? `Among down-payment options (standard schedule), ${downPaymentLeader.label} projects the highest year-30 net worth (${formatUsdPlain(downPaymentLeader.netWorthAt30)}).`
      : null,
    'Mortgage interest and property taxes do not automatically create tax savings — only the amount by which itemizing beats the standard deduction may help, and SALT caps can limit the benefit.',
  ].filter(Boolean);

  return {
    headline,
    bullets,
    framing:
      'This calculator does not say one option is always better. Housing choices depend on cash flow, emergency reserves, tax situation, risk tolerance, local markets, and how long you expect to stay.',
  };
}

export function buildHousingDecisionProjection(raw) {
  const homeValue = parsePositive(raw.homeValue);
  const downPayment = parseNonNegative(raw.downPayment);
  const monthlyRent = parseNonNegative(raw.monthlyRent);
  const annualRentIncrease = parseRatePercent(raw.annualRentIncrease);
  const annualInterestRate = parseRatePercent(raw.annualInterestRate);
  const termMonths = Math.round(parsePositive(raw.loanTermMonths) || 0);
  const startDate = parseStartDate(raw.loanStartDate);
  const annualAppreciationRate = parseRatePercent(raw.annualAppreciationRate);
  const annualInvestmentReturn = parseRatePercent(raw.annualInvestmentReturn);
  const propertyTaxRate = parseRatePercent(raw.propertyTaxRate);
  const insuranceRate = parseRatePercent(raw.homeownersInsuranceRate);
  const maintenanceRate = parseRatePercent(raw.maintenanceRate);
  const closingCostRate = parseRatePercent(raw.closingCostRate);
  const sellingCostRate = parseRatePercent(raw.sellingCostRate);
  const monthlyHoa = parseNonNegative(raw.monthlyHoa) ?? 0;

  const filingStatus = raw.filingStatus || 'mfj';
  const federalMarginalRate = DEFAULT_TAX_ASSUMPTIONS.federalMarginalRate;
  const includeSaltCap = DEFAULT_TAX_ASSUMPTIONS.includeSaltCap;
  const standardDeduction = standardDeductionForStatus(filingStatus);
  const saltCap = DEFAULT_TAX_ASSUMPTIONS.saltCap;
  const estimatedAnnualStateTax = DEFAULT_TAX_ASSUMPTIONS.stateTaxForSalt;
  const useItemizedEstimate = true;

  if (
    homeValue == null
    || downPayment == null
    || monthlyRent == null
    || annualRentIncrease == null
    || annualInterestRate == null
    || !(termMonths > 0)
    || !startDate
    || annualAppreciationRate == null
    || annualInvestmentReturn == null
    || propertyTaxRate == null
    || insuranceRate == null
    || maintenanceRate == null
    || closingCostRate == null
    || sellingCostRate == null
    || downPayment > homeValue
    || closingCostRate > 0.06 + 1e-9
  ) {
    return null;
  }

  const originalDownPct = downPayment / homeValue;
  // PMI defaults conceptually to 0.5% under 20% down; if user clears it, treat as 0.
  let pmiRate = parseRatePercent(raw.pmiRate);
  if (pmiRate == null) pmiRate = originalDownPct < 0.2 ? 0.005 : 0;
  if (originalDownPct >= 0.2) {
    // Force $0 PMI path when down ≥ 20% (rate may still display for education).
  }

  const loanAmount = roundMoney(homeValue - downPayment);
  const closingCosts = roundMoney(homeValue * closingCostRate);
  const defaultAvailableCash = roundMoney(downPayment + closingCosts);
  const availableCash = parseNonNegative(raw.availableCash) ?? defaultAvailableCash;
  const cashToBuy = roundMoney(downPayment + closingCosts);
  const buySideInvest = roundMoney(Math.max(0, availableCash - cashToBuy));
  const rentStartingInvest = roundMoney(availableCash);

  const monthlyRate = annualInterestRate / 12;
  const monthlyAppreciation = annualAppreciationRate / 12;
  const monthlyInvestReturn = annualInvestmentReturn / 12;
  const basePayment = loanAmount > 0 ? excelPmt(monthlyRate, termMonths, loanAmount, 0, 1) : 0;

  const horizonMonths = 30 * 12;
  const buyShared = {
    homeValue0: homeValue,
    loanAmount,
    downPayment,
    monthlyRate,
    termMonths,
    basePayment,
    startDate,
    monthlyAppreciation,
    monthlyInvestReturn,
    monthlyRent0: monthlyRent,
    annualRentIncrease,
    pmiRate: originalDownPct >= 0.2 ? 0 : pmiRate,
    propertyTaxRate,
    insuranceRate,
    maintenanceRate,
    monthlyHoa,
    sellingCostRate,
    horizonMonths,
    startingSideInvest: buySideInvest,
  };

  const buyPaths = {};
  PAYMENT_STRATEGIES.forEach((strategy) => {
    buyPaths[strategy.id] = buildBuySchedule({
      ...buyShared,
      paymentStrategy: strategy.id,
      id: strategy.id,
      label: strategy.label,
    });
  });

  const buy = buyPaths.standard;
  const buyOneExtra = buyPaths.one_extra;
  const buyTwoExtra = buyPaths.two_extra;
  const buyBiweekly = buyPaths.biweekly;

  const paymentStrategies = Object.fromEntries(
    PAYMENT_STRATEGIES.map((strategy) => {
      const path = buyPaths[strategy.id];
      return [
        strategy.id,
        {
          ...strategy,
          payoffDate: path.payoffDate,
          payoffMonth: path.payoffMonth,
          totalInterest: path.totalInterest,
          interestSaved: roundMoney(buy.totalInterest - path.totalInterest),
          monthsSaved: Math.max(0, (buy.payoffMonth || termMonths) - (path.payoffMonth || termMonths)),
          netWorthAt30: rowAtYear(path.rows, 30)?.netWorth ?? 0,
          scheduledMonthlyPi: path.scheduledMonthlyPi,
        },
      ];
    })
  );

  const rent = buildRentSchedule({
    startingInvest: rentStartingInvest,
    monthlyRent0: monthlyRent,
    annualRentIncrease,
    monthlyInvestReturn,
    buyRows: buy.rows,
    horizonMonths,
    startDate,
  });

  const startingOwnCost = buy.costBreakdown.total;
  const monthlyCashFlowGap = roundMoney(startingOwnCost - monthlyRent);
  const monthlyInvestedIfRenting = roundMoney(Math.max(0, monthlyCashFlowGap));
  const monthlyInvestedIfBuying = roundMoney(Math.max(0, -monthlyCashFlowGap));

  // Annual tax table + attach cumulative tax benefit into buy net worth for comparison tables.
  const taxTable = MILESTONE_YEARS.map((year) => {
    const interest = sumInterestInYear(buy.rows, year);
    const propertyTax = sumFieldInYear(buy.rows, year, 'monthlyPropertyTax');
    const estimate = estimateAnnualTaxBenefit({
      mortgageInterest: interest,
      propertyTax,
      estimatedStateTax: estimatedAnnualStateTax,
      useItemizedEstimate,
      includeSaltCap,
      saltCap,
      standardDeduction,
      federalMarginalRate,
    });
    return {
      year,
      mortgageInterest: roundMoney(interest),
      propertyTaxes: roundMoney(propertyTax),
      ...estimate,
      note: estimate.exceedsStandard
        ? 'Itemizing may exceed the standard deduction under these assumptions.'
        : 'Standard deduction appears better under these assumptions.',
    };
  });

  // Cumulative tax benefit by year (sum of annual benefits through that year).
  const cumTaxBenefitByYear = {};
  let runningTaxBenefit = 0;
  for (let y = 1; y <= 30; y += 1) {
    const interest = sumInterestInYear(buy.rows, y);
    const propertyTax = sumFieldInYear(buy.rows, y, 'monthlyPropertyTax');
    const estimate = estimateAnnualTaxBenefit({
      mortgageInterest: interest,
      propertyTax,
      estimatedStateTax: estimatedAnnualStateTax,
      useItemizedEstimate,
      includeSaltCap,
      saltCap,
      standardDeduction,
      federalMarginalRate,
    });
    runningTaxBenefit += estimate.incrementalTaxBenefit;
    cumTaxBenefitByYear[y] = roundMoney(runningTaxBenefit);
  }

  const adjustedBuyNw = (year) => {
    const row = rowAtYear(buy.rows, year);
    if (!row) return 0;
    return roundMoney(row.netWorth + (cumTaxBenefitByYear[year] || 0));
  };

  // Best path among rent vs standard buy (extra-payment strategies are shown separately).
  const pathNw = (path, year = 30) => {
    if (path.id === 'standard' || path.id === 'buy') return adjustedBuyNw(year);
    const row = rowAtYear(path.rows, year);
    return row?.netWorth ?? 0;
  };
  const bestPathObj = pathNw(buy, 30) >= pathNw(rent, 30) ? buy : rent;

  const buyNw30 = adjustedBuyNw(30);
  const rentNw30 = pathNw(rent, 30);
  const buyVsRentDeltaAt30 = roundMoney(buyNw30 - rentNw30);

  let breakEvenYear = null;
  let prevDelta = adjustedBuyNw(1) - pathNw(rent, 1);
  for (let year = 2; year <= 30; year += 1) {
    const delta = adjustedBuyNw(year) - pathNw(rent, year);
    if (Math.sign(delta) !== 0 && Math.sign(prevDelta) !== 0 && Math.sign(delta) !== Math.sign(prevDelta)) {
      breakEvenYear = year;
      break;
    }
    prevDelta = delta;
  }

  const customPct = homeValue > 0 ? downPayment / homeValue : 0;
  const downOptions = [];
  DOWN_PAYMENT_PERCENTS.forEach((pct) => {
    downOptions.push(
      buildDownPaymentOption({
        homeValue,
        downPct: pct,
        label: `${Math.round(pct * 100)}% down`,
        availableCash,
        closingCostRate,
        monthlyRate,
        termMonths,
        startDate,
        monthlyAppreciation,
        monthlyInvestReturn,
        monthlyRent0: monthlyRent,
        annualRentIncrease,
        pmiRate: pct >= 0.2 ? 0 : pmiRate,
        propertyTaxRate,
        insuranceRate,
        maintenanceRate,
        monthlyHoa,
        sellingCostRate,
        horizonMonths,
        milestoneYears: [5, 10, 20, 30],
      })
    );
  });
  if (!DOWN_PAYMENT_PERCENTS.some((pct) => Math.abs(pct - customPct) < 0.005)) {
    downOptions.push(
      buildDownPaymentOption({
        homeValue,
        downPct: customPct,
        label: 'Your down payment',
        availableCash,
        closingCostRate,
        monthlyRate,
        termMonths,
        startDate,
        monthlyAppreciation,
        monthlyInvestReturn,
        monthlyRent0: monthlyRent,
        annualRentIncrease,
        pmiRate: customPct >= 0.2 ? 0 : pmiRate,
        propertyTaxRate,
        insuranceRate,
        maintenanceRate,
        monthlyHoa,
        sellingCostRate,
        horizonMonths,
        milestoneYears: [5, 10, 20, 30],
      })
    );
  }
  downOptions.sort((a, b) => a.downPct - b.downPct);
  const downPaymentLeader = downOptions.reduce(
    (best, opt) => (opt.netWorthAt30 > best.netWorthAt30 ? opt : best),
    downOptions[0]
  );

  const yearTable = MILESTONE_YEARS.map((year) => {
    const b = rowAtYear(buy.rows, year);
    const r = rowAtYear(rent.rows, year);
    const buyNw = adjustedBuyNw(year);
    const rentNw = r?.netWorth ?? 0;
    return {
      year,
      buy: b,
      rent: r,
      oneExtra: rowAtYear(buyOneExtra.rows, year),
      twoExtra: rowAtYear(buyTwoExtra.rows, year),
      biweekly: rowAtYear(buyBiweekly.rows, year),
      cumulativeTaxBenefit: cumTaxBenefitByYear[year] || 0,
      buyNetWorth: buyNw,
      rentNetWorth: rentNw,
      buyVsRent: roundMoney(buyNw - rentNw),
    };
  });

  const chartSeries = CHART_YEARS.map((year) => {
    if (year === 0) {
      return {
        year: 0,
        buyNetWorth: buySideInvest,
        rentNetWorth: rentStartingInvest,
        buyEquity: downPayment,
        buyAfterSaleEquity: afterSaleEquity(homeValue, loanAmount, sellingCostRate),
        rentInvested: rentStartingInvest,
        standardNetWorth: buySideInvest,
        oneExtraNetWorth: buySideInvest,
        twoExtraNetWorth: buySideInvest,
        biweeklyNetWorth: buySideInvest,
        ownMonthly: buy.costBreakdown.total,
        rentMonthly: monthlyRent,
        taxBenefit: 0,
        standardDeduction,
      };
    }
    const b = rowAtYear(buy.rows, year);
    const r = rowAtYear(rent.rows, year);
    const taxRow = taxTable.find((row) => row.year === year);
    return {
      year,
      buyNetWorth: adjustedBuyNw(year),
      rentNetWorth: r?.netWorth ?? 0,
      buyEquity: b?.equityAmount ?? 0,
      buyAfterSaleEquity: b?.afterSaleEquity ?? 0,
      rentInvested: r?.investmentValue ?? 0,
      standardNetWorth: b?.netWorth ?? 0,
      oneExtraNetWorth: rowAtYear(buyOneExtra.rows, year)?.netWorth ?? 0,
      twoExtraNetWorth: rowAtYear(buyTwoExtra.rows, year)?.netWorth ?? 0,
      biweeklyNetWorth: rowAtYear(buyBiweekly.rows, year)?.netWorth ?? 0,
      ownMonthly: b?.monthlyHousingCost ?? 0,
      rentMonthly: r?.monthlyRent ?? 0,
      taxBenefit: taxRow?.incrementalTaxBenefit ?? 0,
      standardDeduction,
    };
  });

  const projection = {
    assumptions: {
      homeValue,
      downPayment,
      loanAmount,
      closingCosts,
      cashToBuy,
      availableCash,
      buySideInvest,
      rentStartingInvest,
      monthlyRent,
      annualRentIncrease,
      annualInterestRate,
      termMonths,
      startDate: formatIsoDate(startDate),
      annualAppreciationRate,
      annualInvestmentReturn,
      pmiRate: originalDownPct >= 0.2 ? 0 : pmiRate,
      pmiRateInput: pmiRate,
      propertyTaxRate,
      insuranceRate,
      maintenanceRate,
      closingCostRate,
      sellingCostRate,
      monthlyHoa,
      defaultAvailableCash,
      filingStatus,
      federalMarginalRate,
      estimatedAnnualStateTax,
      useItemizedEstimate,
      includeSaltCap,
      standardDeduction,
      saltCap,
      growthNote:
        'Home appreciation and investment returns use simple monthly rates (annual ÷ 12). Rent increases once per year on the lease anniversary. Monthly investing uses only the cash-flow difference between owning and renting. Bi-weekly is modeled as 26 half-payments per year (≈ 13 monthly payments).',
    },
    buy,
    rent,
    buyOneExtra,
    buyTwoExtra,
    buyBiweekly,
    paymentStrategies,
    ownershipCostBreakdown: buy.costBreakdown,
    cashFlowInvest: {
      monthlyOwnCost: startingOwnCost,
      monthlyRent: roundMoney(monthlyRent),
      monthlyGap: monthlyCashFlowGap,
      monthlyInvestedIfRenting,
      monthlyInvestedIfBuying,
      investsWhenRenting: monthlyInvestedIfRenting > 0,
      investsWhenBuying: monthlyInvestedIfBuying > 0,
    },
    upfrontCash: {
      downPayment: roundMoney(downPayment),
      closingCosts,
      cashToBuy,
      availableCash,
      cashInvestedIfRenting: rentStartingInvest,
      cashLeftIfBuying: buySideInvest,
    },
    monthlyPayment: roundMoney(basePayment),
    monthlyCostToOwn: buy.costBreakdown.total,
    monthlyRent: roundMoney(monthlyRent),
    monthlyPmi: buy.costBreakdown.pmi,
    monthlyPropertyTax: buy.costBreakdown.propertyTax,
    buyNetWorthAt30: buyNw30,
    rentNetWorthAt30: rentNw30,
    buyVsRentDeltaAt30,
    bestPath: bestPathObj.id === 'standard' ? 'buy' : bestPathObj.id,
    bestPathLabel: bestPathObj.id === 'standard' || bestPathObj.id === 'buy' ? 'Buy' : bestPathObj.label,
    bestNetWorthAt30: pathNw(bestPathObj, 30),
    breakEvenYear,
    payoffDate: buy.payoffDate,
    totalInterest: buy.totalInterest,
    downPaymentOptions: downOptions,
    downPaymentLeader,
    yearTable,
    taxTable,
    chartSeries,
  };

  projection.interpretation = buildInterpretation(projection);
  return projection;
}

/**
 * First of the month, two months after today — advancing by 3 calendar months
 * so a mid-July view defaults to October 1 (not the current month).
 */
export function defaultLoanStartDate(now = new Date()) {
  const start = new Date(now.getFullYear(), now.getMonth() + 3, 1);
  return `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-01`;
}

export { formatUsdPlain, rowAtYear, roundMoney };
